/**
 * Space Task Visibility
 *
 * Centralizes member-scoped Space task visibility.
 *
 * Primary responsibilities:
 * - Let Space owners/admins see all tasks
 * - Limit regular members to tasks they created or are assigned to
 *
 * Dependencies:
 * - Task assignee helpers
 */

import type { AllowedSpaceRole } from "@/lib/auth/space-permissions"
import { getTaskAssigneeIds } from "@/lib/spaces/task-assignees"
import type { SpaceTaskWithAssignees } from "@/types/spaces"

type SpaceTaskVisibilitySource = Pick<
  SpaceTaskWithAssignees,
  "assigned_to" | "custom_fields" | "user_id"
> &
  Partial<Pick<SpaceTaskWithAssignees, "assignee_ids">>

export type SpaceTaskVisibilityContext = {
  userId: string
  role: AllowedSpaceRole | null
}

export type SpaceTaskVisibilityScope = "all" | "me"

const FULL_TASK_VISIBILITY_ROLES = new Set<AllowedSpaceRole>([
  "owner",
  "admin"
])

export function canViewAllSpaceTasks(role: AllowedSpaceRole | null) {
  return role ? FULL_TASK_VISIBILITY_ROLES.has(role) : false
}

export function canViewSpaceTask(
  task: SpaceTaskVisibilitySource,
  context: SpaceTaskVisibilityContext
) {
  if (task.user_id === context.userId) {
    return true
  }

  return getTaskAssigneeIds(task).includes(context.userId)
}

export function filterSpaceTasksForUser<T extends SpaceTaskVisibilitySource>(
  tasks: T[],
  context: SpaceTaskVisibilityContext
) {
  return tasks.filter(task => canViewSpaceTask(task, context))
}
